import {
  CalendarDay,
  addCalendarDays,
  calendarDayOfWeek,
  diffCalendarDays,
  startOfIsoCalendarWeek,
} from '@shared/models/dateTypes';

/**
 * How often a repeat falls (#1138): every week (`w1`, the default), every second or third week
 * counted from the first day's week (`w2`, `w3`), or one week of the month — the first … fourth or
 * the last of the chosen weekday in its month (`m1` … `m4`, `mlast`).
 */
export type RepeatInterval = 'w1' | 'w2' | 'w3' | 'm1' | 'm2' | 'm3' | 'm4' | 'mlast';

/** The choices as the form lists them, the default first. */
export const REPEAT_INTERVALS: readonly { value: RepeatInterval; label: string }[] = [
  { value: 'w1', label: 'Hver uge' },
  { value: 'w2', label: 'Hver 2. uge' },
  { value: 'w3', label: 'Hver 3. uge' },
  { value: 'm1', label: 'Første uge i måneden' },
  { value: 'm2', label: 'Anden uge i måneden' },
  { value: 'm3', label: 'Tredje uge i måneden' },
  { value: 'm4', label: 'Fjerde uge i måneden' },
  { value: 'mlast', label: 'Sidste uge i måneden' },
];

/** Whether `day` falls in the interval, counted from `start` (its week for `w2`/`w3`). */
function inInterval(day: CalendarDay, start: CalendarDay, interval: RepeatInterval): boolean {
  if (interval === 'w1') return true;
  if (interval === 'w2' || interval === 'w3') {
    const weeks = diffCalendarDays(startOfIsoCalendarWeek(day), startOfIsoCalendarWeek(start)) / 7;
    return weeks % (interval === 'w2' ? 2 : 3) === 0;
  }
  const dayOfMonth = Number(day.slice(8, 10));
  if (interval === 'mlast') return addCalendarDays(day, 7).slice(0, 7) !== day.slice(0, 7);
  // The n-th of its weekday in the month: days 1–7 are the first, 8–14 the second, …
  return Math.ceil(dayOfMonth / 7) === Number(interval.slice(1));
}

/**
 * The days in `[start, end]` falling on one of `weekdays` — the set a repeated booking is
 * generated for. `weekdays` uses 0 = Sunday, as `calendarDayOfWeek` and `Date` both do.
 *
 * **Both ends are inclusive.** The form seeds `start` with the source period's own day, so that
 * day is generated too — and flagged by the caller as overlapping, like any other day that
 * collides with a period already on the booking.
 *
 * Pure calendar arithmetic: no instant is constructed, so the walk cannot slide on a DST
 * transition, and `<=` on "YYYY-MM-DD" is chronological by construction.
 */
export function repeatDays(
  start: CalendarDay,
  end: CalendarDay,
  weekdays: number[],
  interval: RepeatInterval = 'w1',
): CalendarDay[] {
  const days: CalendarDay[] = [];

  for (let day = start; day <= end; day = addCalendarDays(day, 1)) {
    if (weekdays.includes(calendarDayOfWeek(day)) && inInterval(day, start, interval)) {
      days.push(day);
    }
  }

  return days;
}
